import { forwardRef, type RefObject, useCallback } from 'react';
import { getClasses } from '@pasqal/core/helpers/styles';
import { useTitleRef } from '~/systems/title';
import Icon from '../Icon/Icon';
import type { ButtonHTMLAttributes, CSSProperties } from 'react';
import './IconButton.css';

interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  icon: string;
  label: string;
  tooltip?: string;
  size?: number;
  background?: 'none' | 'always' | 'hover';
  square?: boolean;
  padding?: number;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      tooltip,
      size = 20,
      background = 'none',
      square = false,
      padding,
      className,
      style,
      ...rest
    },
    externalRef
  ) {
    const titleRef = useTitleRef<HTMLButtonElement>(tooltip || label);

    // Merge the internal titleRef with any externally provided ref
    const mergedRef = useCallback(
      (node: HTMLButtonElement | null) => {
        (titleRef as RefObject<HTMLButtonElement | null>).current = node;
        if (typeof externalRef === 'function') {
          externalRef(node);
        } else if (externalRef) {
          (externalRef as RefObject<HTMLButtonElement | null>).current = node;
        }
      },
      [titleRef, externalRef]
    );

    const classes = getClasses([
      'IconButton2',
      background !== 'none'
        ? `IconButton2--background${background.charAt(0).toUpperCase() + background.slice(1)}`
        : '',
      square ? 'IconButton2--square' : '',
      className ?? ''
    ]);

    const btnStyle: CSSProperties = {
      ...(padding != null ? { padding: `${padding}px` } : {}),
      ...style
    };

    return (
      <button
        ref={mergedRef}
        aria-label={label}
        className={classes}
        style={btnStyle}
        {...rest}
      >
        <Icon kind={icon} size={size} />
      </button>
    );
  }
);

export default IconButton;
